import React from "react";
import { MIN_ANSWERS_LENGTH } from "features/Library/Common/constants";
import {
  type ValidationContext,
  type IAnswerBase,
  type ValidationValueBase,
  type IQuestion,
} from "features/Library/Common/models";
import { type AnswersBuilderPropsBase } from "features/Library/Common/props";
import { get } from "lodash";
import ObjectUtils from "utils/objectUtils";
import styles from "features/Library/Common/QuestionsForm/Answers/AnswersBuilders/AnswersBuilderBase.module.scss";
import { RemoveButton } from "features/Library/Common/QuestionsForm/RemoveButton/RemoveButton";

export type AnswersBuilderBaseProps = AnswersBuilderPropsBase<IQuestion<any>> & {
  defaultDataProvider: (questionType: number) => IAnswerBase;
  renderAnswer: (
    answer: IAnswerBase,
    index: number,
    validatedFieldProps: ValidationContext<ValidationValueBase>,
    propertyNamePrefix: string,
  ) => React.ReactNode;
  renderAdditionalOptions?: (
    validatedFieldProps: ValidationContext<ValidationValueBase>,
    propertyNamePrefix: string,
  ) => React.ReactElement | null;
  renderAddAnswerButton?: (onClick: () => void) => React.ReactElement | null;
  renderAddOtherAnswerButton?: () => React.ReactElement | null;
};

export const AnswersBuilderBase = (props: AnswersBuilderBaseProps) => {
  const {
    question,
    arrayHelpers,
    disabled,
    renderAnswer,
    validatedFieldProps,
    defaultDataProvider,
    propertyNamePrefix,
    renderAdditionalOptions,
    onChanged,
    renderAddAnswerButton,
    renderAddOtherAnswerButton,
  } = props;

  const onAddAnswer = () => {
    arrayHelpers.push(defaultDataProvider(question.questionType));
    setTimeout(() => {
      onChanged?.();
    }, 0);
  };

  const onRemoveAnswer = (index: number) => () => {
    arrayHelpers.remove(index);
    setTimeout(() => {
      onChanged?.();
    }, 0);
  };

  return (
    <>
      {question.answers.map((answer, index: number) => (
        <React.Fragment key={answer.id ?? index}>
          <div className={styles.fields}>
            {renderAnswer(answer, index, validatedFieldProps, `${propertyNamePrefix}answers.${index}.`)}
            {!disabled && index >= MIN_ANSWERS_LENGTH && <RemoveButton onClick={onRemoveAnswer(index)} />}
          </div>
        </React.Fragment>
      ))}

      <div className={styles.additional}>{renderAdditionalOptions?.(validatedFieldProps, propertyNamePrefix)}</div>

      <div className={styles.buttons}>
        {renderAddAnswerButton?.(onAddAnswer)}
        {renderAddOtherAnswerButton?.()}
      </div>
    </>
  );
};

/* istanbul ignore next */
const getPropsToCompare = (props: AnswersBuilderBaseProps) => {
  const { arrayHelpers, validatedFieldProps, ...rest } = props;
  const errors = get(props.validatedFieldProps.errors, `${props.propertyNamePrefix}answers`);
  const touched = get(props.validatedFieldProps.touched, `${props.propertyNamePrefix}answers`);

  return { ...rest, errors, touched };
};

/* istanbul ignore next */
export default React.memo(AnswersBuilderBase, (prev, next) => {
  return ObjectUtils.shallowEqual(getPropsToCompare(prev), getPropsToCompare(next));
});
